<template>
    <div class="flex flex-col h-full gap-2 p-2 pt-0">
        <UTabs
            v-model="selectedTab"
            color="neutral"
            variant="link"
            :content="false"
            :items="items"
            :ui="{
                trigger: 'grow',
                root: 'mb-2'
            }"
        >
            <template #default="{ item }">
                <div class="flex items-center gap-1.5 flex-1">
                    <span>{{ item.label }}</span>
                    <UIcon
                        v-if="tabErrors[item.value as IB_TAB]"
                        name="i-ph-warning-circle-fill"
                        class="text-error text-lg"
                    />
                </div>
            </template>
        </UTabs>

        <csv-to-grid
            v-if="selectedTab === IB_TAB.ACCOUNTS"
        />
        <csv-to-grid
            v-else-if="selectedTab === IB_TAB.CUSTOMERS"
            is-sub-account
            is-customer
        />
        <csv-to-grid
            v-else-if="selectedTab === IB_TAB.SUPPLIERS"
            is-sub-account
        />
        <div v-else>Unknown tab {{ selectedTab }}</div>

        <div class="flex justify-end gap-2">
            <div class="flex items-center">
                <UCheckbox v-model="importedAccountsOnly" label="Aktiver kun importerte kontoer" />
                <UTooltip
                    text="Om denne er på aktiveres kun kontoer som importeres i inngående balanse. Inaktive kontoer kan aktiveres igjen under kontoplan."
                    :delay-duration="0"
                    :ui="{ content: 'w-72 h-fit', text: 'whitespace-pre-line p-1' }"
                >
                    <i class="block mx-2 i-ph-info" />
                </UTooltip>
            </div>
            <UModal
                v-model:open="modalOpen"
                :ui="{
                    content: 'overflow-visible z-50',
                }"
            >
                <UButton
                    size="lg"
                    class="w-fit self-end"
                    label="Gå videre"
                    :disabled="!allValid"
                    @click="modalOpen = true"
                />
                <template #content>
                    <div class="grid grid-cols-2 gap-4 p-4">
                        <div class="text-lg">Importer inngående balanse</div>
                        <UButton
                            size="lg"
                            variant="ghost"
                            color="neutral"
                            class="ml-auto mb-2"
                            icon="i-ph-x"
                            @click="modalOpen = false"
                        />
                        <span class="text-sm">Dato</span>
                        <DatePicker
                            v-model="selectedDate"
                            class="h-fit z-40"
                            :disable-background-color-class="true"
                        />
                        <span class="text-sm">Beskrivelse</span>
                        <UInput
                            v-model="description"
                            size="lg"
                            class="w-full"
                            placeholder="Inngående balanse"
                        />
                        <UButton
                            size="lg"
                            class="w-fit self-end ml-auto col-start-2 mt-6"
                            label="Importer"
                            :disabled="!selectedDate || isWorking"
                            @click="importIB"
                        />
                    </div>
                </template>
            </UModal>
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, ref} from 'vue'
import CsvToGrid from '@/incoming-balance/CsvToGrid.vue'
import {createTransactionLines, deactivateOtherAccounts} from '@/incoming-balance/incomingBalance.composable'
import useTrialBalance from '@/incoming-balance/tribal.composable'
import subAccountComposable from '@/incoming-balance/subaccount.composable'
const {useCustomerBalance, useSupplierBalance} = subAccountComposable()
import DatePicker from '@/dateUtils/DatePicker.vue'
import {useTransactionCreator} from '@/transaction/create-transaction/TransactionCreator.service'
import {notifyError, notifySuccess} from '@/services/infrastructure/notification.service'
import {formatCurrency} from '@/services/formatter.service'
import {ORG_SWITCHED} from '@/types/events'
import {useEmitter} from '@/services/infrastructure/emitter'
import {IB_TAB} from '@/incoming-balance/types/IBTab.type'

const {onEvent} = useEmitter()

const importedAccountsOnly = ref(false)

const {
    tribalData,
    entries: tbEntries,
    totalBalance: tbTotal,
    customerTotal: tbCustomerTotal,
    supplierTotal: tbSupplierTotal,
    missingAccounts,
    clear: clearTribal
} = useTrialBalance()

const {
    subAccData: customerData,
    entries: customerEntries,
    totalBalance: customerTotal,
    deviations: customerDeviations,
    clear: clearCustomer,
    duplicatedInvoiceNos: duplicatedCustomerInvoiceNos,
}  = useCustomerBalance()

const {
    subAccData: supplierData,
    entries: supplierEntries,
    totalBalance: supTotal,
    deviations: supplierDeviations,
    clear: clearSupplier,
    duplicatedInvoiceNos: duplicatedSupplierInvoiceNos,
} = useSupplierBalance()

const {saveTransaction} = useTransactionCreator()

const balanceAccountValid = computed(() =>
    formatCurrency(tbTotal.value) === '0,00' &&
    tbCustomerTotal.value === customerTotal.value &&
    tbSupplierTotal.value === supTotal.value &&
    missingAccounts.value === 0
)

const customerValid = computed(() =>
    tbCustomerTotal.value === customerTotal.value &&
    customerDeviations.missingSubAccounts.value === 0 &&
    duplicatedCustomerInvoiceNos.value === 0
)

const supplierValid = computed(() =>
    tbSupplierTotal.value === supTotal.value &&
    supplierDeviations.missingSubAccounts.value === 0 &&
    duplicatedSupplierInvoiceNos.value === 0
)

const allValid = computed(() =>
    !(tribalData.value.csvData.length === 0 && customerData.value.csvData.length === 0 && supplierData.value.csvData.length === 0) &&
    balanceAccountValid.value &&
    customerValid.value &&
    supplierValid.value
)

const items = ref([
    {
        label: 'Saldobalanse',
        icon: 'i-ph-scales',
        value: IB_TAB.ACCOUNTS
    },
    {
        label: 'Kundereskontro',
        icon: 'i-ph-users',
        value: IB_TAB.CUSTOMERS
    },
    {
        label: 'Leverandørreskontro',
        icon: 'i-ph-truck',
        value: IB_TAB.SUPPLIERS
    }
])

const tabErrors = computed(() => ({
    [IB_TAB.ACCOUNTS]: !balanceAccountValid.value,
    [IB_TAB.CUSTOMERS]: !customerValid.value,
    [IB_TAB.SUPPLIERS]: !supplierValid.value,
}))

const selectedTab = ref(IB_TAB.ACCOUNTS)
const selectedDate = ref()
const description = ref('Inngående balanse')
const isWorking = ref(false)
const modalOpen = ref(false)

const importIB = async () => {
    isWorking.value = true
    try {
        if(importedAccountsOnly.value) {
            await deactivateOtherAccounts(tbEntries.value)
        }
        const transactionLines = createTransactionLines(selectedDate.value, description.value, tbEntries, customerEntries, supplierEntries)
        await saveTransaction([], transactionLines)
        clearAll()
        notifySuccess({ title: 'Suksess', description: 'Inngående balanse importert.' })
    } catch (error: any) {
        notifyError({ title: 'Feilet',  description: {descriptionMessage: `Importen feilet feilet grunnet: ${error.response.data.detail}`, error} })
    } finally {
        isWorking.value = false
        modalOpen.value = false
    }
}

const clearAll = () => {
    clearTribal()
    clearCustomer()
    clearSupplier()
}

onEvent(ORG_SWITCHED, clearAll)

</script>

<style>

</style>